import { zxcvbn, zxcvbnOptions } from '@zxcvbn-ts/core';
import * as zxcvbnCommonPackage from '@zxcvbn-ts/language-common';
import styled from 'styled-components';

import { IconCheckmarkSmall } from '@components/icons/checkmark-small';
import { IconCloseSmall } from '@components/icons/close-small';
import { IconIndicatorSmall } from '@components/icons/indicator-small';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';
import { Color, theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';
import { transitionSlow } from '@utils/transitions';
import { isOnlyDigits } from '@utils/validators';

const MIN_LENGTH = 6;

export type PasswordStrengthBarProps = {
  password: string;
};

export const PasswordStrengthBar = ({ password }: PasswordStrengthBarProps) => {
  const { t } = useScopedTranslate(locale);
  setZxcvbnOptions();

  const isPasswordTooShort = password.length < MIN_LENGTH;
  const score = Math.min(isPasswordTooShort ? 0 : zxcvbn(password).score, 3);

  const scoreWords = isPasswordTooShort
    ? t('passwordStrength')
    : [t('veryWeak'), t('weak'), t('good'), t('strong')][score];

  const barColors = [
    theme.color.red100,
    theme.color.orange100,
    theme.color.blue100,
    theme.color.green100,
  ];

  return (
    <Stack space={1}>
      <BarContainer>
        {[0, 1, 2, 3].map((index) => (
          <Bar
            key={index}
            isFilled={!isPasswordTooShort && index <= score}
            color={isPasswordTooShort ? theme.color.uiTrans5 : barColors[score]}
          />
        ))}
      </BarContainer>
      <Text font="regular14" text={scoreWords} color="textHelper" style={{ textAlign: 'end' }} />
    </Stack>
  );
};

type State = 'neutral' | 'positive' | 'negative';

const stateToColor: Record<State, Color> = {
  neutral: 'textSecondary',
  positive: 'elementGreen200',
  negative: 'elementRed200',
};

const stateToIcon: Record<State, JSX.Element> = {
  neutral: <IconIndicatorSmall color="textSecondary" />,
  positive: <IconCheckmarkSmall color="green200" />,
  negative: <IconCloseSmall color="red200" />,
};

export const ValidationMessage = ({
  isPositive,
  isActive,
  text,
}: {
  isPositive: boolean;
  isActive: boolean;
  text: string;
}) => {
  const state: State = isActive ? (isPositive ? 'positive' : 'negative') : 'neutral';
  return (
    <Stack space={2} orientation="horizontal" align="center">
      {stateToIcon[state]}
      <Text color={stateToColor[state]} text={text} />
    </Stack>
  );
};

export const usePasswordValidations = (password: string, passwordConfirm?: string) => {
  setZxcvbnOptions();
  const hasPasswordConfirm = passwordConfirm !== undefined;
  const isPasswordCheckActive = password.length > 0;
  const isLongEnough = password.length >= 12;
  const validation = zxcvbn(password);
  const notCommon =
    validation.feedback.warning === null || validation.feedback.warning === 'userInputs';
  const notOnlyNumbers = isPasswordCheckActive && !isOnlyDigits(password);
  const isMatch = hasPasswordConfirm ? password === passwordConfirm : true;
  const isAllValid = isLongEnough && notCommon && notOnlyNumbers && isMatch;

  const ValidationMessages = () => {
    const { t } = useScopedTranslate(locale);
    return (
      <div>
        <ValidationMessage
          isPositive={isLongEnough}
          isActive={isPasswordCheckActive}
          text={t('atLeast12Characters')}
        />
        <ValidationMessage
          isPositive={notCommon}
          isActive={isPasswordCheckActive}
          text={t('notCommon')}
        />
        <ValidationMessage
          isPositive={notOnlyNumbers}
          isActive={isPasswordCheckActive}
          text={t('notNumbersOnly')}
        />
        {hasPasswordConfirm && (
          <ValidationMessage
            isPositive={isMatch}
            isActive={isPasswordCheckActive}
            text={t('passwordsMatch')}
          />
        )}
      </div>
    );
  };

  return {
    isPasswordCheckActive,
    isLongEnough,
    notCommon,
    notOnlyNumbers,
    isMatch,
    isAllValid,
    ValidationMessages,
  };
};

const BarContainer = styled.div({
  display: 'flex',
  gap: theme.space[2],
});

const Bar = styled.div<{ isFilled: boolean; color: string }>((p) => ({
  flex: '1',
  height: pxToRem(4),
  backgroundColor: p.isFilled ? p.color : theme.color.uiTrans5,
  borderRadius: '2px',
  transition: transitionSlow('background-color'),
}));

const locale = {
  en: {
    passwordStrength: 'Password strength',
    veryWeak: 'Very weak',
    weak: 'Weak',
    good: 'Good',
    strong: 'Strong',
    atLeast12Characters: 'At least 12 characters',
    notCommon: 'Not common password',
    notNumbersOnly: 'Not numbers only',
    passwordsMatch: 'Passwords match',
  },
  is: {
    passwordStrength: 'Styrkur lykilorðs',
    veryWeak: 'Mjög veikt',
    weak: 'Veikt',
    good: 'Gott',
    strong: 'Sterkt',
    atLeast12Characters: 'Að minnsta kosti 12 stafir',
    notCommon: 'Ekki algengt lykilorð',
    notNumbersOnly: 'Má ekki innihalda einungis tölur',
    passwordsMatch: 'Lykilorðin verða að passa',
  },
};

let hasSetOptions = false;

function setZxcvbnOptions() {
  if (!hasSetOptions) {
    zxcvbnOptions.setOptions(zxcvbnCommonPackage);
    hasSetOptions = true;
  }
}
